<template>
  <ListComponent title="Emergency Orders" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="5" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @keyup.enter="searchList"
            @click:clear="() => { filters.search = ''; searchList(); }"
            class="mr-2"
          ></v-text-field>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(appliedFilters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>
            <span v-if="Object.keys(appliedFilters).length == 0">Filters</span>
            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(_, key) in appliedFilters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}<br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>

          <Columns :headers="headers" :gridColInstance="gridColumnApi" />
        </v-col>

        <v-col cols="7" class="px-0 pt-0 pb-2 d-flex justify-end align-center" style="gap:6px">
          <v-tooltip top>
            <template #activator="{}">
              <!-- <v-btn depressed color="primary" small class="mr-3" :loading="creatingEToken"
                @click="autoCreateEToken">
                <v-icon small class="mr-1">mdi-lightning-bolt</v-icon>
                Auto Create E-Token
              </v-btn> -->
            </template>
            <span>Generate a new Emergency Order with a dummy E-Token</span>
          </v-tooltip>

          <!-- Quick date filter chips -->
          <v-chip-group v-model="activeDateChip" mandatory active-class="primary white--text" class="mr-2 pa-0">
            <v-chip small @click="setDateFilter('today')">Today</v-chip>
            <v-chip small @click="setDateFilter('7days')">Last 7 Days</v-chip>
            <v-chip small @click="setDateFilter('30days')">Last 30 Days</v-chip>
            <v-chip small @click="setDateFilter('all')">All</v-chip>
          </v-chip-group>


          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                color="secondary"
                small
                class="mr-3"
                v-on="on"
                v-bind="attrs"
                @click="showExportDialog = true"
              >
                <v-icon small class="mr-1">mdi-microsoft-excel</v-icon>
                Export
              </v-btn>
            </template>
            <span>Download Emergency Orders in XLSX format</span>
          </v-tooltip>

          <!-- Export Date Range Dialog -->
          <v-dialog v-model="showExportDialog" max-width="420" persistent>
            <v-card>
              <v-card-title class="primary py-2">
                <span class="text-subtitle-1 font-weight-medium white--text">Export Emergency Orders</span>
                <v-spacer />
                <v-btn icon dark small @click="showExportDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pt-4 pb-0">
                <v-row>
                  <v-col cols="12" class="Form-Columns-Dense">
                    <v-radio-group
                      v-model="exportType"
                      row
                      hide-details
                      class="mt-0"
                    >
                      <v-radio label="Orders" value="orders" />
                      <v-radio label="Containers" value="containers" />
                    </v-radio-group>
                  </v-col>
                  <v-col cols="6" class="Form-Columns-Dense">
                    <DateField
                      :inputProps="{
                        dense: true,
                        label: 'From Date',
                        'hide-details': 'auto',
                        clearable: true,
                      }"
                      v-model="exportDateFrom"
                    />
                  </v-col>
                  <v-col cols="6" class="Form-Columns-Dense">
                    <DateField
                      :inputProps="{
                        dense: true,
                        label: 'To Date',
                        'hide-details': 'auto',
                        clearable: true,
                      }"
                      v-model="exportDateTo"
                    />
                  </v-col>
                  <v-col cols="12" class="Form-Columns-Dense">
                    <span class="caption grey--text">Leave blank to export all records (with current filters applied).</span>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions class="justify-end pb-3 pr-4">
                <v-btn depressed @click="showExportDialog = false">Cancel</v-btn>
                <v-btn
                  color="primary"
                  depressed
                  :loading="exporting"
                  @click="exportData"
                >
                  <v-icon small class="mr-1">mdi-microsoft-excel</v-icon>
                  Download
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>

      <!-- Insights chips -->
      <v-row class="ma-0 mb-2" v-if="insights">
        <v-col cols="12" class="px-0 py-0 d-flex flex-wrap" style="gap:8px">
          <v-chip small label color="blue lighten-5" class="blue--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-file-document-outline</v-icon>
            Orders: {{ insights.total_orders }}
          </v-chip>
          <v-chip small label color="teal lighten-5" class="teal--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-login</v-icon>
            Gate In: {{ insights.total_gate_in }}
          </v-chip>
          <v-chip small label color="teal lighten-5" class="teal--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-logout</v-icon>
            Gate Out: {{ insights.total_gate_out }}
          </v-chip>
          <v-chip small label color="orange lighten-5" class="orange--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-file-check-outline</v-icon>
            DAs: {{ insights.total_das }}
          </v-chip>
          <v-chip small label color="purple lighten-5" class="purple--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-truck-fast-outline</v-icon>
            Trips: {{ insights.total_trips }}
          </v-chip>
          <v-chip small label color="amber lighten-5" class="amber--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-truck-outline</v-icon>
            Sub-Hire: {{ insights.total_sub_hire }}
          </v-chip>
          <v-chip small label color="blue-grey lighten-5" class="blue-grey--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-truck-check-outline</v-icon>
            Own Fleet: {{ insights.total_own_fleet }}
          </v-chip>
          <v-chip small label color="green lighten-5" class="green--text text--darken-3 font-weight-bold">
            <v-icon x-small left>mdi-handshake-outline</v-icon>
            Sub-Hire Jobs: {{ insights.total_subhire }}
          </v-chip>
        </v-col>
      </v-row>

      <v-col cols="12" class="pa-0">
        <AgGridVue
          style="width: 100%; height: calc(100vh - 210px)"
          class="ag-theme-alpine"
          id="myGrid"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          @columnMoved="resizeGrid"
          @columnVisible="resizeGrid"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :suppressDragLeaveHidesColumns="true"
          :rowData="allEmergencyOrders"
        ></AgGridVue>
      </v-col>
    </template>

    <template #dialogs>
      <BulkSubhireUpdate
        v-model="showBulkSubhireForm"
      />
      <EmergencyOrderFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="applyFilters"
      />
      <ConfirmDADialog
        v-model="showConfirmDADialog"
        :orderId="confirmDAOrderId"
        :sourceLocation="confirmDASourceLocation"
        :orderInfo="confirmDAOrderInfo"
        @daConfirmed="getEmergencyOrderList"
      />
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import EmergencyOrderFilters from "@/components/ModuleBased/dialogs/filters/EmergencyOrderFilters.vue";
import EmergencyOrderButton from "@/components/AgGridComponents/EmergencyOrderButton.vue";
import EmergencyOrderStatusChip from "@/components/AgGridComponents/EmergencyOrderStatusChip.vue";
import ConfirmDADialog from "@/components/ModuleBased/dialogForms/ConfirmDADialog.vue";
import BulkSubhireUpdate from "@/components/ModuleBased/dialogForms/BulkSubhireUpdate.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    AgGridVue,
    Pagination,
    agColumnHeader: CustomHeader,
    EmergencyOrderFilters,
    EmergencyOrderButton,
    EmergencyOrderStatusChip,
    ConfirmDADialog,
    BulkSubhireUpdate,
    DateField,
  },
  data() {
    return {
      sorting: {},
      showFilterDialog: false,
      appliedFilters: {},
      showConfirmDADialog: false,
      showBulkSubhireForm: false,
      exporting: false,
      showExportDialog: false,
      exportType: "orders",
      exportDateFrom: null,
      exportDateTo: null,
      confirmDAOrderId: null,
      confirmDASourceLocation: null,
      confirmDAOrderInfo: {},
      headers: [
        {
          headerName: "E-Token",
          field: "e_token",
          tooltipField: "e_token",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "VBS Ref No",
          field: "vbs_reference_number",
          tooltipField: "vbs_reference_number",
          minWidth: 150,
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 130,
          cellRendererFramework: "EmergencyOrderStatusChip",
          cellStyle: { display: "flex", alignItems: "center" },
        },
        {
          headerName: "Stage",
          field: "stage",
          sortable: true,
          minWidth: 110,
          cellRendererFramework: "EmergencyOrderStatusChip",
          cellStyle: { display: "flex", alignItems: "center" },
        },
        {
          headerName: "Customer",
          field: "customer_name",
          tooltipField: "customer_name",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Vendor",
          field: "vendor_name",
          tooltipField: "vendor_name",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Trip ID",
          field: "trip_id",
          tooltipField: "trip_id",
          sortable: true,
          minWidth: 200,
          cellRenderer: (params) => {
            const list = params.data.trip_list;
            if (!list || !list.length) return "-";
            const container = document.createElement("div");
            list.forEach((trip, index) => {
              const span = document.createElement("span");
              span.innerText = trip.trip_id;
              span.className = "primary--text font-weight-bold cursor-pointer";
              span.style.cursor = "pointer";
              span.addEventListener("click", () => {
                this.$router.push({ name: "app-admin-trip-details-id", params: { id: trip.id } }).catch(() => {});
              });
              container.appendChild(span);
              if (index < list.length - 1) {
                container.appendChild(document.createTextNode(", "));
              }
            });
            return container;
          },
        },
        {
          headerName: "DA No",
          field: "da_no",
          tooltipField: "da_no",
          minWidth: 200,
          cellRenderer: (params) => {
            const list = params.data.da_list;
            if (!list || !list.length) return "-";
            const container = document.createElement("div");
            list.forEach((da, index) => {
              const span = document.createElement("span");
              span.innerText = da.da_no;
              span.className = "primary--text font-weight-bold cursor-pointer";
              span.onclick = (e) => {
                e.stopPropagation();
                params.context.parentComponent.$router
                  .push({
                    name: "app-admin-delivery-advice-details-id",
                    params: { id: da.id },
                  })
                  .catch(() => {});
              };
              container.appendChild(span);
              if (index < list.length - 1) {
                container.appendChild(document.createTextNode(", "));
              }
            });
            return container;
          },
        },
        {
          headerName: "Vehicle No",
          field: "vehicle_number",
          tooltipField: "vehicle_number",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Driver",
          field: "driver_name",
          tooltipField: "driver_name",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Sub Hire",
          field: "is_sub_hire",
          minWidth: 100,
          cellRenderer: (params) => (params.value ? "Yes" : "No"),
        },
        {
          headerName: "OOG / HAZ / Reefer",
          field: "is_oog",
          minWidth: 180,
          cellRenderer: (params) => {
            const flags = [];
            if (params.data.is_oog)
              flags.push(
                '<span style="background:#FFE0D0;color:#BF360C;border-radius:3px;padding:1px 6px;font-size:11px;font-weight:700">OOG</span>',
              );
            if (params.data.is_hazardous)
              flags.push(
                '<span style="background:#FFCDD2;color:#B71C1C;border-radius:3px;padding:1px 6px;font-size:11px;font-weight:700">HAZ</span>',
              );
            if (params.data.is_reefer)
              flags.push(
                '<span style="background:#E1F5FE;color:#0277BD;border-radius:3px;padding:1px 6px;font-size:11px;font-weight:700">REEFER</span>',
              );
            return flags.length ? flags.join(" ") : "-";
          },
        },
        {
          headerName: "Sub Hire ID",
          field: "sub_hire_id",
          minWidth: 180,
          cellRenderer: (params) => {
            const raw = params.data.sub_hire_pk;
            if (!raw) return "-";
            // Normalise to array: [{id, sub_hire_id}, ...]
            const list = Array.isArray(raw)
              ? raw
              : [{ id: raw, sub_hire_id: params.data.sub_hire_id }];
            const container = document.createElement("div");
            list.forEach((shj, index) => {
              const span = document.createElement("span");
              span.innerText = shj.sub_hire_id;
              span.className = "primary--text font-weight-bold";
              span.style.cursor = "pointer";
              span.addEventListener("click", (e) => {
                e.stopPropagation();
                this.$router
                  .push({
                    name: "app-admin-sub-hire-completed",
                    query: { search: shj.sub_hire_id },
                  })
                  .catch(() => {});
              });
              container.appendChild(span);
              if (index < list.length - 1) {
                container.appendChild(document.createTextNode(", "));
              }
            });
            return container;
          },
        },
        {
          headerName: "Order Date",
          field: "order_date",
          tooltipField: "order_date",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Pickup Arrival",
          field: "pickup_arrival",
          tooltipField: "pickup_arrival",
          minWidth: 150,
        },
        {
          headerName: "Pickup Departure",
          field: "pickup_departure",
          tooltipField: "pickup_departure",
          minWidth: 160,
        },
        {
          headerName: "Drop Arrival",
          field: "drop_arrival",
          tooltipField: "drop_arrival",
          minWidth: 150,
        },
        // { headerName: "Drop Departure", field: "drop_departure", tooltipField: "drop_departure", minWidth: 150 },  // hidden — process completes at drop_gate_in
        {
          headerName: "Source Location",
          field: "source_location_name",
          tooltipField: "source_location_name",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Destination Location",
          field: "destination_location_name",
          tooltipField: "destination_location_name",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Container No",
          field: "container_no",
          minWidth: 160,
          cellRenderer: (params) => {
            const val = params.value;
            if (!val || !Array.isArray(val) || !val.length) return "-";
            return val.map((c) => c.container_no || "-").join(", ");
          },
        },
        {
          headerName: "Container Type",
          field: "container_no",
          minWidth: 160,
          cellRenderer: (params) => {
            const val = params.value;
            if (!val || !Array.isArray(val) || !val.length) return "-";
            return val.map((c) => c.container_type_name || "-").join(", ");
          },
        },
        {
          headerName: "Created",
          field: "created",
          tooltipField: "created",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "System Blockers",
          field: "failure_reasons",
          minWidth: 200,
          tooltipField: "failure_reasons",
          cellRenderer: (params) => {
            if (!params.value) return "-";
            return `<span style="color:#E53935;font-weight:600;" title="${params.value}">⚠ ${params.value}</span>`;
          },
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "EmergencyOrderButton",
          pinned: "right",
          minWidth: 180,
        },
      ],

      allEmergencyOrders: [],
      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,
      filters: {},
      insights: null,
      activeDateChip: 3, // default = All

      gridApi: null,
      gridColumnApi: null,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
        onCellClicked: (params) => {
          const colDef = params.colDef;
          if (colDef && colDef.onCellClicked) {
            colDef.onCellClicked(params);
          }
        },
      },

      selectedOrderId: null,
      detailsDrawer: false,
      permissions: null,
      creatingEToken: false,
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  watch: {
    showExportDialog(isOpen) {
      if (!isOpen) {
        this.exportDateFrom = null;
        this.exportDateTo = null;
        this.exportType = "orders";
      }
    },
  },
  methods: {
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }
      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1,
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`,
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    searchList() {
      this.pageNo = 1;
      this.getEmergencyOrderList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getEmergencyOrderList();
    },
    prevPage() {
      this.pageNo--;
      this.getEmergencyOrderList();
    },
    nextPage() {
      this.pageNo++;
      this.getEmergencyOrderList();
    },
    resetFilters() {
      this.filters = {};
      this.appliedFilters = {};
      this.itemsPerPageChanged(20);
    },
    applyFilters() {
      this.appliedFilters = { ...this.filters };
      this.itemsPerPageChanged(20);
    },
    openConfirmDA(id) {
      const row = this.allEmergencyOrders.find((o) => o.id === id);
      this.confirmDAOrderId = id;
      this.confirmDASourceLocation = row ? row.source_location : null;
      this.confirmDAOrderInfo = {
        eToken: row?.e_token ?? null,
        sourceLocation: row?.source_location_name ?? null,
        customerName: row?.customer_name ?? null,
        customerId: row?.customer ?? null,
        laneId: row?.lane ?? null,
        laneName: row?.lane_title ?? null,
        vehicleNumber: row?.vehicle_number ?? null,
        driverName: row?.driver_name ?? null,
        containerData: row?.container_data ?? [],
        isOog: row?.is_oog ?? false,
        isHazardous: row?.is_hazardous ?? false,
      };
      this.showConfirmDADialog = true;
    },
    openCreateDA(id) {
      // Navigate to detail page — Create DA is managed there
      this.viewDetails(id);
    },
    viewDetails(id) {
      this.$router
        .push({
          name: "app-admin-emergency-order-details-id",
          params: { id },
        })
        .catch(() => {});
    },
    autoCreateEToken() {
      this.creatingEToken = true;
      const mockPayload = {
        vehicle_number: "MOCK-VH-001",
        driver_name: "Mock Driver",
        container_no: [
          { container_no: "MOCK-CONT-001" },
          { container_no: "MOCK-CONT-002" },
        ],
      };
      this.$api.emergencyOrder
        .generateDummy(mockPayload)
        .then((res) => {
          const eToken = res.data?.e_token || res.data?.id || "";
          this.$bus.$emit("showToastMessage", {
            message: `Emergency Order created: ${eToken}`,
            color: "success",
          });
          this.getEmergencyOrderList();
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Failed to create Emergency Order.",
            color: "error",
          });
        })
        .finally(() => {
          this.creatingEToken = false;
        });
    },
    exportData() {
      this.exporting = true;
      const params = { ...this.appliedFilters, limit: "all" };
      if (this.exportDateFrom) params.movement_date_after = this.exportDateFrom;
      if (this.exportDateTo) params.movement_date_before = this.exportDateTo;
      const isContainerExport = this.exportType === "containers";
      const request = isContainerExport
        ? this.$api.emergencyOrder.exportContainersToExcel(params)
        : this.$api.emergencyOrder.exportToExcel(params);
      const filenamePrefix = isContainerExport
        ? "Emergency_Order_Containers"
        : "Emergency_Orders";
      request
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${filenamePrefix}_${new Date().getTime()}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          this.exporting = false;
          this.showExportDialog = false;
        })
        .catch((err) => {
          this.exporting = false;
          const msg =
            err?.response?.data?.detail || "Failed to export data";
          this.$bus.$emit("showToastMessage", {
            message: msg,
            color: "error",
          });
        });
    },
    setDateFilter(range) {
      const today = new Date();
      const fmt = (d) => d.toISOString().split("T")[0];
      if (range === "today") {
        this.filters.order_date_after = fmt(today);
        this.filters.order_date_before = fmt(today);
      } else if (range === "7days") {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        this.filters.order_date_after = fmt(from);
        this.filters.order_date_before = fmt(today);
      } else if (range === "30days") {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        this.filters.order_date_after = fmt(from);
        this.filters.order_date_before = fmt(today);
      } else {
        delete this.filters.order_date_after;
        delete this.filters.order_date_before;
      }
      this.pageNo = 1;
      this.getEmergencyOrderList();
    },
    getEmergencyOrderList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.emergencyOrder
        .getEmergencyOrderList(params)
        .then((res) => {
          this.allEmergencyOrders = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
      // Fetch insights with same filters (no pagination)
      const insightParams = { ...this.filters };
      this.$api.emergencyOrder
        .getInsights(insightParams)
        .then((res) => { this.insights = res.data || res; })
        .catch(() => {});
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`,
        );
      } else {
        this.permissions = { ...permissions };
        this.setDateFilter("all");
      }
    }
  },
};
</script>
